import { clstx } from '@payaca/utilities/cvaMerge';
import { ComponentPropsWithRef } from 'react';

export type ProseProps = ComponentPropsWithRef<'div'>;

/**
 * Typography for content we don't author: rendered Markdown, inbound email,
 * document HTML. A `div` that styles the raw headings, paragraphs, links,
 * code and tables inside it from the shared type scale.
 *
 * ## When to use
 *
 * - Wrapping markup whose tags we don't write ourselves, so `Heading` and
 *   `Text` can't be used.
 *
 * ## When to choose another component
 *
 * - **Copy we write** — headings use `H1`–`H4`, body copy uses `Text`. Never
 *   wrap authored copy in `Prose` to style it.
 *
 * ## Composition rules
 *
 * Wrap only the dynamic part. Keep surrounding titles and labels outside, as
 * `Heading` and `Text`. Unlike them, `Prose` takes `className` and every other
 * `div` prop, so layout and spacing classes go on it directly.
 *
 * ✅ Correct:
 *
 * ```tsx
 * <H3>Description</H3>
 * <Prose>
 *   <MarkdownLabel markdown={description} />
 * </Prose>
 * ```
 *
 * ❌ Wrong:
 *
 * ```tsx
 * <Prose>
 *   <h3>Description</h3>
 *   <p>Written by us, so this should be Text.</p>
 * </Prose>
 * ```
 */
const Prose = ({ className, ...rest }: ProseProps) => (
  <div {...rest} className={clstx('typography:prose', className)} />
);

export default Prose;
